import { normalize } from 'normalizr';
import { ActionTree } from 'vuex';
import { progress as progressSchema } from '@descomplica/dex-store/lib/normalizr_schema';
import { dexMutation, dexQuery } from '@/helpers/graphql';
import { ProgressState } from './state';
import RootState from '@/store/root_state';
import {
  CreateProgressMutation,
  getUserFirstProgress,
  ProgressByUserIdAndListItemId,
} from '../../queries/dexQueries';
import { ErrorHandler } from '@/helpers/errorHandler';
import i18n from '../../../i18n';
import SentryClient from '@/helpers/sentry';
import { GetUserFirstProgressResponse } from '@/store/queries/interfaces';

const actions: ActionTree<ProgressState, RootState> = {
  async CHECK_PROGRESS({ commit }, payload) {
    const { listItemId, userId } = payload;

    try {
      const response = await dexQuery(ProgressByUserIdAndListItemId, {
        listItemId,
        userId,
      });

      if (!response?.data?.progressByUserIdAndListItemId) {
        return false;
      }

      const normalizedResponse = normalize(
        { progress: response.data.progressByUserIdAndListItemId },
        {
          progress: progressSchema,
        },
      );

      commit('SET_PROGRESSES', normalizedResponse.entities);

      return true;
    } catch (error) {
      SentryClient.captureError(error);

      return false;
    }
  },
  async CREATE_PROGRESS({ commit, dispatch }, payload) {
    const { listItemId, userId } = payload;

    try {
      const hasProgress = await dispatch('CHECK_PROGRESS', {
        listItemId,
        userId,
      });

      if (hasProgress) {
        return true;
      }

      const response = await dexMutation(CreateProgressMutation, {
        listItemId,
        userId,
      });

      const normalizedResponse = normalize(response.data.createProgress, {
        progress: progressSchema,
      });

      commit('SET_PROGRESSES', normalizedResponse.entities);

      return true;
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('itWasNotPossibleToCreateProgress').toString(),
      });

      return false;
    }
  },
  async GET_FIRST_CONTENT_CONSUMED_DATE({ commit }, payload: { userId: number }) {
    try {
      const response = await dexQuery<GetUserFirstProgressResponse>(getUserFirstProgress, {
        userId: payload.userId,
      });

      const dateString = response.data.userById.progressesByUserId.nodes[0].createdAt ?? '';

      if (!dateString) return;

      commit('SET_FIRST_CONTENT_CONSUMED_DATE', { date: new Date(dateString) });
    } catch (error) {
      SentryClient.captureError(error);
    }
  },
};

export default actions;
